import React, { useState, Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  MagnifyingGlassIcon,
  QrCodeIcon,
  CheckIcon,
  PrinterIcon,
  ArrowsUpDownIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { axiosInstance } from "../utils/axios";
import DashboardQrCode from "./DashboardQrCode";
import useDialogStore from "../stores/useDialogStore";
import PrintByIds from "./PrintByIds";
import GetDeviceInfo from "./GetDeviceInfo";
import VerifyDevicesManually from "./VerifyDevicesManually";
import MoveDevices from "./MoveDevices";
import { getUser } from "../utils/storage";
import Button from "./Button";
import excelIcon from "../assets/excel-icon.webp";
import { toast } from "react-toastify";

export default function GlobalDeviceSearch() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [multipleQueries, setMultipleQueries] = useState("");
  const { openDialog } = useDialogStore();
  const user = getUser();
  const userRole = user?.role;
  const [showMobileActions, setShowMobileActions] = useState(false);

  const navigate = useNavigate();

  const {
    data: multipleSearchResults = [],
    isLoading: multipleSearchLoading,
    error: multipleSearchError,
    refetch: executeMultipleSearch,
  } = useQuery({
    queryKey: ["deviceSearch", multipleQueries, "multiple"],
    queryFn: async () => {
      if (!multipleQueries.trim()) return [];
      const queryString = encodeURIComponent(
        multipleQueries.split("\n").filter(Boolean).join(",")
      );
      const { data } = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device/lookup/multiple?query=${queryString}`
      );
      return data;
    },
    enabled: false,
  });

  const exportMutation = useMutation({
    mutationFn: async (queries) => {
      const queryArray = queries.split("\n").filter(Boolean);

      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/multiple-devices`,
        {
          query: queryArray,
        },
        {
          responseType: "blob",
        }
      );
      return response;
    },
    onSuccess: (response) => {
      // Create and download the file
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Device_Export_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up

      toast.success("Export completed successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export. Please try again.");
    },
  });

  const handleSearch = () => {
    if (!multipleQueries.trim()) return;
    executeMultipleSearch();
  };

  const handleExport = () => {
    if (!multipleQueries.trim()) {
      toast.warning("Please enter some queries to export");
      return;
    }

    exportMutation.mutate(multipleQueries);
  };

  const handleResultClick = (
    type,
    unique_id,
    status,
    spare_parts,
    recycled,
    invoice_number,
    invoice_id
  ) => {
    if (status === "reserved") {
      navigate(
        `/admin/stock/reserved?unique_id=${unique_id}&invoice_number=${invoice_number}&invoice_id=${invoice_id}`
      );
    } else if (status === "sold") {
      navigate(
        `/admin/stock/sold?unique_id=${unique_id}&invoice_number=${invoice_number}&invoice_id=${invoice_id}`
      );
    } else if (spare_parts) {
      navigate(`/admin/spare-parts?unique_id=${unique_id}`);
    } else if (recycled) {
      navigate(`/admin/stock/history/recycled?unique_id=${unique_id}`);
    } else {
      navigate(`/admin/devices/${type}-stock?unique_id=${unique_id}`);
    }
    setSearchOpen(false);
  };

  const SearchSkeleton = () => (
    <div className="animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-2 border-b">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="fixed top-0 w-full z-50 flex-row gap-2 bg-bgBlue pt-5 md:py-5 md:px-12 px-4 flex">
        <button
          type="button"
          className="flex items-center rounded-md bg-white px-3 py-2 w-56 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => setSearchOpen(true)}
        >
          <MagnifyingGlassIcon
            className="h-5 w-5 text-gray-400 mr-2"
            aria-hidden="true"
          />
          <span>Search...</span>
          <span className="ml-2 text-gray-400 text-xs">(Ctrl + K)</span>
        </button>
        <div className="hidden lg:flex items-center gap-2">
          <Button
            onClick={() =>
              openDialog({ title: "Qr Scanner", content: <DashboardQrCode /> })
            }
            variant="outline"
            size="sm"
            icon={<QrCodeIcon className="h-5 w-5" />}
            className="text-blue-600 border-blue-600 bg-blue-50 hover:bg-blue-100"
          >
            <span className="hidden lg:block">Qr Scanner</span>
          </Button>
          {userRole !== "Technician" && (
            <Button
              onClick={() =>
                openDialog({ title: "Print By IDs", content: <PrintByIds /> })
              }
              variant="outline-success"
              size="sm"
              icon={<PrinterIcon className="h-5 w-5" />}
              className="text-green-500 border-green-500 bg-green-50 hover:bg-green-100"
            >
              <span className="hidden md:block">Print By IDs</span>
            </Button>
          )}
          {(userRole == "Admin" ||
            userRole == "Stock Manager" ||
            userRole == "SuperAdmin") && (
            <Button
              onClick={() =>
                openDialog({
                  title: "Get Device Info",
                  content: <GetDeviceInfo />,
                })
              }
              variant="outline-orange"
              size="sm"
              icon={<QrCodeIcon className="h-5 w-5" />}
              className="text-orange-500 border-orange-500 bg-orange-50 hover:bg-orange-100"
            >
              <span className="hidden md:block">Get Device Info</span>
            </Button>
          )}
          {userRole !== "Technician" && (
            <Button
              onClick={() =>
                openDialog({
                  title: "Verify Devices Manually",
                  content: <VerifyDevicesManually />,
                })
              }
              variant="outline-info"
              size="sm"
              icon={<CheckIcon className="h-5 w-5" />}
              className="text-indigo-500 border-indigo-500 bg-indigo-50 hover:bg-indigo-500"
            >
              <span className="hidden md:block">Verify Devices Manually</span>
            </Button>
          )}

          {userRole !== "Technician" && (
            <Button
              onClick={() =>
                openDialog({ title: "Move Devices", content: <MoveDevices /> })
              }
              variant="outline"
              size="sm"
              icon={<ArrowsUpDownIcon className="h-5 w-5" />}
              className="text-pink-600 border-pink-600 bg-pink-50 hover:bg-pink-600"
            >
              <span className="hidden md:block">Move Devices</span>{" "}
            </Button>
          )}
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <Button
            onClick={() => setShowMobileActions(true)}
            variant="primary"
            size="icon"
            icon={<PlusIcon className="h-5 w-5" />}
          />
        </div>
      </div>

      <Transition.Root show={searchOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={setSearchOpen}>
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-2xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                <div className="relative">
                  <div className="relative p-4">
                    <div className="mb-2 text-sm text-gray-700">
                      Enter serial numbers or IDs (paste a list or press Enter
                      after each one)
                    </div>
                    <textarea
                      className="w-full border rounded-md p-2 min-h-[100px] text-gray-900 placeholder:text-gray-400 text-sm"
                      placeholder="Type or paste and press Enter..."
                      value={multipleQueries}
                      onChange={(e) => setMultipleQueries(e.target.value)}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedData = e.clipboardData.getData("Text");
                        setMultipleQueries((prev) => prev + " " + pastedData);
                      }}
                      autoFocus
                    />
                    <div className="text-xs text-gray-500 mb-3">
                      Paste a list of values or press Enter after each entry
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => setMultipleQueries("")}
                        variant="outline"
                        size="sm"
                        disabled={!multipleQueries.trim()}
                      >
                        Clear
                      </Button>
                      <Button
                        onClick={handleSearch}
                        variant="primary"
                        size="sm"
                        disabled={!multipleQueries.trim()}
                      >
                        Search Multiple
                      </Button>
                      <Button
                        onClick={handleExport}
                        variant="outline-success"
                        size="sm"
                        icon={excelIcon}
                        disabled={
                          !multipleQueries.trim() || exportMutation.isPending
                        }
                      >
                        {exportMutation.isPending ? (
                          <div className="flex items-center gap-2">
                            <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                            Exporting...
                          </div>
                        ) : (
                          "Export to Excel"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex divide-x divide-gray-100">
                  <div className="max-h-96 min-w-0 flex-auto overflow-y-auto px-6 py-4">
                    {multipleSearchLoading ? (
                      <SearchSkeleton />
                    ) : multipleSearchError ? (
                      <div className="text-sm text-red-500">
                        Multiple search failed. Please try again.
                      </div>
                    ) : multipleSearchResults?.data?.length > 0 ? (
                      <>
                        {/* Search Results */}
                        <ul className="divide-y divide-gray-100">
                          {multipleSearchResults.data.map((result) => (
                            <li
                              key={result.id}
                              className="p-2 cursor-pointer hover:bg-gray-100 rounded flex items-center gap-4"
                              onClick={() =>
                                handleResultClick(
                                  result.type,
                                  result.unique_id,
                                  result.status,
                                  result.spare_parts,
                                  result.recycled,
                                  result?.invoice_number,
                                  result?.invoice_id
                                )
                              }
                            >
                              <div className="text-gray-400">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="w-6 h-6"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"
                                  />
                                </svg>
                              </div>
                              <div className="flex gap-4">
                                {result.serial_no && (
                                  <div>
                                    <span className="font-bold text-sm">
                                      Serial:
                                    </span>{" "}
                                    {result.serial_no}
                                  </div>
                                )}
                                <div>
                                  <span className="font-bold text-sm">
                                    Unique ID:
                                  </span>{" "}
                                  {result.unique_id}
                                </div>

                                <div>
                                  <span className="font-bold text-sm">
                                    Type:
                                  </span>{" "}
                                  {result.type === "blue" && (
                                    <span className="inline-block bg-blue-200 bg-opacity-30 text-blue-800 text-xs px-2 py-1 rounded-md">
                                      Blue
                                    </span>
                                  )}
                                  {result.type === "green" && (
                                    <span className="inline-block bg-green-200 bg-opacity-30 text-green-800 text-xs px-2 py-1 rounded-md">
                                      Green
                                    </span>
                                  )}
                                  {result.type === "white" && (
                                    <span className="inline-block bg-gray-200 bg-opacity-30 text-gray-800 text-xs px-2 py-1 rounded-md">
                                      White
                                    </span>
                                  )}
                                </div>
                                {result.status && (
                                  <div>
                                    <span className="font-bold text-sm">
                                      Status:
                                    </span>{" "}
                                    <span className="inline-block bg-yellow-200 bg-opacity-30 text-yellow-800 text-xs px-2 py-1 rounded-md">
                                      {result.status}
                                    </span>
                                  </div>
                                )}
                                {result.invoice_number && (
                                  <div>
                                    <span className="font-bold text-sm">
                                      Invoice Number:
                                    </span>{" "}
                                    <span className="inline-block bg-purple-200 bg-opacity-30 text-purple-800 text-xs px-2 py-1 rounded-md">
                                      {result.invoice_number}
                                    </span>
                                  </div>
                                )}
                                {result.recycled && (
                                  <div className="inline-block bg-blue-200 text-blue-800 text-xs px-2 py-1 rounded-md">
                                    Recycled
                                  </div>
                                )}
                                {result.spare_parts && (
                                  <div className="inline-block bg-green-200 text-green-800 text-xs px-2 py-1 rounded-md">
                                    Spare Parts
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>

                        {/* Summary and Not Found Section */}
                        <div className="mt-4 border-t border-gray-200">
                          {/* Not Found Items */}
                          {multipleSearchResults.not_found &&
                            Object.keys(multipleSearchResults.not_found)
                              .length > 0 && (
                              <div className="mt-3">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                  Not Found:
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(
                                    multipleSearchResults.not_found
                                  ).map(([key, value]) => (
                                    <span
                                      key={key}
                                      className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded-md border border-red-200"
                                    >
                                      {value}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-gray-500">
                        No multiple search results found
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {showMobileActions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg p-6 w-11/12 max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Actions</h2>
              <Button
                onClick={() => setShowMobileActions(false)}
                variant="light"
                size="icon"
                className="text-gray-500 hover:text-gray-700 p-1"
                icon={
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                }
                aria-label="Close"
              />
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  openDialog({
                    title: "Qr Scanner",
                    content: <DashboardQrCode />,
                  });
                  setShowMobileActions(false);
                }}
                variant="outline"
                size="md"
                icon={<QrCodeIcon className="h-5 w-5" />}
                className="text-blue-600 border-blue-600 bg-blue-50 hover:bg-blue-100 w-full justify-start"
              >
                Qr Scanner
              </Button>
              {userRole !== "Technician" && (
                <Button
                  onClick={() => {
                    openDialog({
                      title: "Print By IDs",
                      content: <PrintByIds />,
                    });
                    setShowMobileActions(false);
                  }}
                  variant="outline-success"
                  size="md"
                  icon={<PrinterIcon className="h-5 w-5" />}
                  className="text-green-500 border-green-500 bg-green-50 hover:bg-green-100 w-full justify-start"
                >
                  Print By IDs
                </Button>
              )}
              {(userRole === "Admin" ||
                userRole === "Stock Manager" ||
                userRole === "SuperAdmin") && (
                <Button
                  onClick={() => {
                    openDialog({
                      title: "Get Device Info",
                      content: <GetDeviceInfo />,
                    });
                    setShowMobileActions(false);
                  }}
                  variant="outline-orange"
                  size="md"
                  icon={<QrCodeIcon className="h-5 w-5" />}
                  className="text-orange-500 border-orange-500 bg-orange-50 hover:bg-orange-100 w-full justify-start"
                >
                  Get Device Info
                </Button>
              )}
              {userRole !== "Technician" && (
                <Button
                  onClick={() => {
                    openDialog({
                      title: "Verify Devices Manually",
                      content: <VerifyDevicesManually />,
                    });
                    setShowMobileActions(false);
                  }}
                  variant="outline-info"
                  size="md"
                  icon={<CheckIcon className="h-5 w-5" />}
                  className="text-indigo-500 border-indigo-500 bg-indigo-50 hover:bg-indigo-600 w-full justify-start"
                >
                  Verify Devices Manually
                </Button>
              )}

              {userRole !== "Technician" && (
                <Button
                  onClick={() => {
                    openDialog({
                      title: "Move Devices",
                      content: <MoveDevices />,
                    });
                    setShowMobileActions(false);
                  }}
                  variant="outline"
                  size="md"
                  icon={<ArrowsUpDownIcon className="h-5 w-5" />}
                  className="text-pink-600 border-pink-600 bg-pink-50 hover:bg-pink-600 w-full justify-start"
                >
                  Move Devices
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
